import {
  dietCalorieTotalOf,
  dietDateRange,
  dietLossOf,
  dietPairStreakOf,
  dietProgressOf,
  latestDietLogOf,
  type DietCheerKind,
  type DietGoal,
  type DietLog,
} from "@couples/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Sparkles, Target, Trophy } from "lucide-react";
import { useMemo, useState } from "react";

import DietCalorieChart, { type DietCalorieSeries } from "../components/DietCalorieChart";
import DietDayCard, { type DietDayEntry } from "../components/DietDayCard";
import DietGoalSheet from "../components/DietGoalSheet";
import DietLogSheet from "../components/DietLogSheet";
import DietTrendChart, { type DietChartSeries } from "../components/DietTrendChart";
import QueryState from "../components/QueryState";
import ScreenHeader from "../components/ScreenHeader";
import { useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { useConfirm } from "../lib/confirm";
import { todayDateKey } from "../lib/dates";
import { addCheer, fetchCheers, removeCheer } from "../lib/diet/cheers";
import { fetchGoals, saveGoal, type DietGoalInput } from "../lib/diet/goals";
import { deleteLog, fetchLogs, saveLog, type DietLogInput } from "../lib/diet/logs";
import { fetchReward, saveReward, type DietRewardInput } from "../lib/diet/reward";
import { fetchMembers } from "../lib/media/members";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";

/** グラフの表示日数。1画面に収まる幅で「最近」と「ひと月」を切り替えられれば足りる */
const SPANS: { days: number; label: string }[] = [
  { days: 14, label: "2週間" },
  { days: 30, label: "1か月" },
];

/** 一覧に出す日数。これ以上遡ると、読むものではなくグラフの仕事になる */
const TIMELINE_DAYS = 14;

function dayLabelOf(dateKey: string, todayKey: string, yesterdayKey: string): string {
  if (dateKey === todayKey) return "今日";
  if (dateKey === yesterdayKey) return "昨日";
  const [, month, day] = dateKey.split("-");
  return month === undefined || day === undefined ? dateKey : `${Number(month)}/${Number(day)}`;
}

function signed(value: number): string {
  if (value > 0) return `+${value.toFixed(1)}`;
  if (value < 0) return `-${Math.abs(value).toFixed(1)}`;
  return "±0.0";
}

/**
 * ダイエットタブ。二人で同じ目標に向かって記録し、続くように支え合う。
 *
 * 体重・食事・運動まで couple 単位で相互に見える(→ 20260827121254_diet.sql)。
 * 隠せる余地を残すと見張り合いが成立しないため、この画面には「自分だけの表示」が無い。
 */
export default function Diet() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const selfId = session?.user.id;

  const [span, setSpan] = useState(SPANS[0]?.days ?? 14);
  const [logForm, setLogForm] = useState<{ date: string; log?: DietLog } | null>(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyLogId, setBusyLogId] = useState<string | null>(null);

  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const logs = useQuery({ queryKey: queryKeys.dietLogs, queryFn: () => fetchLogs() });
  const goals = useQuery({ queryKey: queryKeys.dietGoals, queryFn: fetchGoals });
  const cheers = useQuery({ queryKey: queryKeys.dietCheers, queryFn: fetchCheers });
  const reward = useQuery({ queryKey: queryKeys.dietReward, queryFn: fetchReward });

  const todayKey = todayDateKey();
  const yesterdayKey = useMemo(() => {
    const range = dietDateRange(todayKey, 2);
    return range[0] ?? todayKey;
  }, [todayKey]);

  const partnerId = useMemo(
    () => [...(members.data?.keys() ?? [])].find((id) => id !== selfId),
    [members.data, selfId],
  );

  const nameOf = (userId: string) => members.data?.get(userId) ?? "?";
  const colorOf = (userId: string) => colorOfUser(profiles.data, userId, selfId);

  const allLogs = useMemo(() => logs.data ?? [], [logs.data]);
  const goalOf = (userId: string): DietGoal | null =>
    (goals.data ?? []).find((goal) => goal.userId === userId) ?? null;

  const selfGoal = selfId === undefined ? null : goalOf(selfId);
  const partnerGoal = partnerId === undefined ? null : goalOf(partnerId);
  const selfLatest = selfId === undefined ? null : latestDietLogOf(allLogs, selfId);
  const partnerLatest = partnerId === undefined ? null : latestDietLogOf(allLogs, partnerId);

  const totalLoss = dietLossOf(selfGoal, selfLatest) + dietLossOf(partnerGoal, partnerLatest);
  const streak = useMemo(
    () =>
      selfId === undefined || partnerId === undefined
        ? 0
        : dietPairStreakOf(allLogs, selfId, partnerId, todayKey),
    [allLogs, selfId, partnerId, todayKey],
  );

  const selfToday = allLogs.find((log) => log.authorId === selfId && log.loggedOn === todayKey);
  const partnerToday = allLogs.find(
    (log) => log.authorId === partnerId && log.loggedOn === todayKey,
  );

  const chartDates = useMemo(() => dietDateRange(todayKey, span), [todayKey, span]);

  /**
   * 増減の基準。目標が未設定でもグラフだけは描けるよう、
   * 取得できている中でいちばん古い記録に落とす(記録が1件も無ければ描かない)。
   */
  const baselineOf = (userId: string, goal: DietGoal | null): number | null => {
    if (goal !== null) return goal.startWeight;
    const own = allLogs.filter((log) => log.authorId === userId);
    if (own.length === 0) return null;
    return own.reduce((oldest, log) => (log.loggedOn < oldest.loggedOn ? log : oldest)).weight;
  };

  const series = useMemo<DietChartSeries[]>(() => {
    const entries: DietChartSeries[] = [];
    for (const [userId, goal] of [[selfId, selfGoal] as const, [partnerId, partnerGoal] as const]) {
      if (userId === undefined) continue;
      const baseline = baselineOf(userId, goal);
      if (baseline === null) continue;
      entries.push({ userId, name: nameOf(userId), color: colorOf(userId), baseline });
    }
    return entries;
  }, [selfId, partnerId, selfGoal, partnerGoal, allLogs, members.data, profiles.data]);

  /** 摂取カロリーのグラフ用の系列。増減グラフと違い、体重の記録有無に関わらず本人がいれば出す */
  const calorieSeries = useMemo<DietCalorieSeries[]>(() => {
    const entries: DietCalorieSeries[] = [];
    for (const userId of [selfId, partnerId]) {
      if (userId === undefined) continue;
      entries.push({ userId, name: nameOf(userId), color: colorOf(userId) });
    }
    return entries;
  }, [selfId, partnerId, members.data, profiles.data]);

  /** 直近ぶんを日ごとにまとめる。記録が1件も無い日はカードごと出さない */
  const days = useMemo(() => {
    const recent = new Set(dietDateRange(todayKey, TIMELINE_DAYS));
    const byDate = new Map<string, DietLog[]>();
    for (const log of allLogs) {
      if (!recent.has(log.loggedOn)) continue;
      const list = byDate.get(log.loggedOn) ?? [];
      list.push(log);
      byDate.set(log.loggedOn, list);
    }
    return [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [allLogs, todayKey]);

  /** その人の直近の摂取カロリー。未入力の日をまたいで、いちばん新しく入力した日まで遡る */
  const latestCalorieOf = (userId: string): number | null => {
    const ownLogs = [...allLogs]
      .filter((log) => log.authorId === userId)
      .sort((a, b) => b.loggedOn.localeCompare(a.loggedOn));
    for (const log of ownLogs) {
      const total = dietCalorieTotalOf(log);
      if (total !== null) return total;
    }
    return null;
  };

  const cheersOf = (log: DietLog): DietCheerKind[] => {
    const all = cheers.data ?? [];
    // 自分の記録は「もらったぶん」、相手の記録は「自分が押したぶん」を出す
    const mine = log.authorId === selfId;
    return all
      .filter((cheer) => cheer.logId === log.id && (mine || cheer.userId === selfId))
      .map((cheer) => cheer.kind);
  };

  const entriesOf = (dayLogs: DietLog[]): DietDayEntry[] =>
    [...dayLogs]
      // 自分を先に出す。自分の記録を探すのに毎回目で追わなくて済む
      .sort((a, b) => Number(b.authorId === selfId) - Number(a.authorId === selfId))
      .map((log) => {
        const goal = goalOf(log.authorId);
        const baseline = baselineOf(log.authorId, goal);
        return {
          log,
          name: nameOf(log.authorId),
          color: colorOf(log.authorId),
          isSelf: log.authorId === selfId,
          delta: baseline === null ? null : log.weight - baseline,
          cheers: cheersOf(log),
        };
      });

  async function refresh(...keys: readonly (readonly string[])[]) {
    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  }

  async function handleSaveLog(input: DietLogInput) {
    setSaving(true);
    try {
      await saveLog(input);
      await refresh(queryKeys.dietLogs);
      setLogForm(null);
      toast("記録しました。");
    } catch (e) {
      console.error("ダイエットの記録の保存に失敗", e);
      toast("記録の保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteLog(log: DietLog) {
    const ok = await confirm({
      message: `${log.loggedOn.replaceAll("-", "/")}の記録を消します。\n※この操作は取り消せません。`,
      confirmLabel: "消す",
      tone: "danger",
    });
    if (!ok) return;
    setBusyLogId(log.id);
    try {
      await deleteLog(log.id);
      await refresh(queryKeys.dietLogs, queryKeys.dietCheers);
      toast("削除しました。");
    } catch (e) {
      console.error("ダイエットの記録の削除に失敗", e);
      toast("記録の削除に失敗しました。", "error");
    } finally {
      setBusyLogId(null);
    }
  }

  async function handleSaveGoal(goal: DietGoalInput, rewardInput: DietRewardInput) {
    setSaving(true);
    try {
      await saveGoal(goal);
      await saveReward(rewardInput);
      await refresh(queryKeys.dietGoals, queryKeys.dietReward);
      setGoalOpen(false);
      toast("目標を保存しました。");
    } catch (e) {
      console.error("ダイエットの目標の保存に失敗", e);
      toast("目標の保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleCheer(log: DietLog, kind: DietCheerKind) {
    if (selfId === undefined || log.authorId === selfId) return;
    const pressed = (cheers.data ?? []).some(
      (cheer) => cheer.logId === log.id && cheer.userId === selfId && cheer.kind === kind,
    );
    setBusyLogId(log.id);
    try {
      if (pressed) {
        await removeCheer(log.id, selfId, kind);
      } else {
        await addCheer(log.id, kind);
      }
      await refresh(queryKeys.dietCheers);
    } catch (e) {
      console.error("応援スタンプの保存に失敗", e);
      toast("応援の保存に失敗しました。", "error");
    } finally {
      setBusyLogId(null);
    }
  }

  const isPending =
    logs.isPending || goals.isPending || cheers.isPending || reward.isPending || members.isPending;
  const isError = logs.isError || goals.isError || cheers.isError || reward.isError;

  const rewardData = reward.data;
  const rewardRatio =
    rewardData === null || rewardData === undefined
      ? 0
      : Math.min(1, Math.max(0, totalLoss / rewardData.targetLoss));

  const openLogForm = (date: string, log?: DietLog) => {
    setLogForm({ date, log });
  };

  const defaultWeight = selfLatest?.weight ?? selfGoal?.startWeight ?? 60;

  return (
    <>
      <ScreenHeader
        navKey="diet"
        actions={[
          {
            label: "記録",
            icon: Plus,
            onSelect: () => {
              openLogForm(todayKey, selfToday);
            },
            primary: true,
          },
        ]}
        menu={[
          {
            label: "目標とごほうび",
            icon: Target,
            onSelect: () => {
              setGoalOpen(true);
            },
          },
        ]}
      />

      <div className="relative flex-1">
        <div className="flex flex-col gap-3.5 px-4 pt-3.5 pb-24">
          <QueryState
            isPending={isPending}
            isError={isError}
            errorMessage="ダイエットの記録の取得に失敗しました。"
            className="py-10 text-center text-sm"
          />

          {!isPending && !isError && (
            <>
              {/* ふたりのごほうび。個人の目標より上に置き、
                  「相手より減った」ではなく「二人で足したらここまで来た」を先に見せる */}
              {rewardData !== null && rewardData !== undefined ? (
                <button
                  type="button"
                  onClick={() => {
                    setGoalOpen(true);
                  }}
                  className="w-full rounded-xl border border-[var(--color-line)] bg-white p-4 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
                >
                  <span className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]">
                    <Trophy className="size-3.5 text-[var(--color-gold)]" aria-hidden />
                    ふたりのごほうび
                  </span>
                  <span className="mt-1.5 block text-base font-semibold">{rewardData.title}</span>
                  <span className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-bold tabular-nums">
                      {Math.max(0, totalLoss).toFixed(1)}
                    </span>
                    <span className="text-xs text-[var(--color-sub)]">
                      / {rewardData.targetLoss.toFixed(1)} kg
                    </span>
                  </span>
                  <span
                    className="mt-2 block h-2 overflow-hidden rounded-full bg-[var(--color-line)]"
                    role="progressbar"
                    aria-valuenow={Math.round(rewardRatio * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <span
                      className="block h-full rounded-full bg-[var(--color-accent)]"
                      style={{ width: `${String(rewardRatio * 100)}%` }}
                    />
                  </span>
                  <span className="mt-1.5 block text-[11px] text-[var(--color-sub)]">
                    {rewardRatio >= 1
                      ? "達成しました。お祝いの予定を入れましょう。"
                      : `あと ${(rewardData.targetLoss - Math.max(0, totalLoss)).toFixed(1)}kg で達成`}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setGoalOpen(true);
                  }}
                  className="w-full rounded-xl border border-[var(--color-line)] bg-white p-4 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
                >
                  <span className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]">
                    <Trophy className="size-3.5 text-[var(--color-gold)]" aria-hidden />
                    ふたりのごほうび
                  </span>
                  <span className="mt-1.5 block text-sm">
                    目標とごほうびがまだ決まっていません。まずはここから決めましょう。
                  </span>
                </button>
              )}

              {/* 今日の記録。自分が未記録のときは、この画面でいちばん強い操作にする */}
              <div className="rounded-xl border border-[var(--color-line)] bg-white p-4">
                <span className="text-[11px] text-[var(--color-sub)]">今日</span>
                {selfToday === undefined ? (
                  <button
                    type="button"
                    onClick={() => {
                      openLogForm(todayKey);
                    }}
                    className="mt-2 w-full rounded-lg bg-[var(--color-accent)] py-2.5 text-sm font-medium text-white transition hover:opacity-85"
                  >
                    今日の体重を記録する
                  </button>
                ) : (
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold tabular-nums">
                        {selfToday.weight.toFixed(1)}
                      </span>
                      <span className="text-xs text-[var(--color-sub)]">kg</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        openLogForm(todayKey, selfToday);
                      }}
                      className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-xs transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
                    >
                      直す
                    </button>
                  </div>
                )}

                {partnerId !== undefined && (
                  <div className="mt-3 flex items-center gap-2 border-t border-[var(--color-line)] pt-3 text-xs text-[var(--color-sub)]">
                    <span
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ background: colorOf(partnerId) }}
                      aria-hidden
                    />
                    {partnerToday === undefined ? (
                      <span>{nameOf(partnerId)}はまだ記録していません。</span>
                    ) : (
                      <span className="tabular-nums">
                        {nameOf(partnerId)}は記録済み({partnerToday.weight.toFixed(1)}kg)
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ふたりの現在地。目標が未設定の人は、決める入口だけを出す */}
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { userId: selfId, goal: selfGoal, latest: selfLatest, isSelf: true },
                  { userId: partnerId, goal: partnerGoal, latest: partnerLatest, isSelf: false },
                ].map(({ userId, goal, latest, isSelf }) => {
                  if (userId === undefined) return null;
                  const color = colorOf(userId);
                  const loss = dietLossOf(goal, latest);
                  const ratio = dietProgressOf(goal, latest);
                  const rest =
                    goal === null || latest === null
                      ? null
                      : Math.max(0, latest.weight - goal.goalWeight);
                  return (
                    <div
                      key={userId}
                      className="rounded-xl border border-[var(--color-line)] bg-white p-3.5"
                    >
                      <span className="flex items-center gap-1.5">
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{ background: color }}
                          aria-hidden
                        />
                        <span className="truncate text-xs font-semibold">{nameOf(userId)}</span>
                      </span>

                      {latest === null ? (
                        <p className="mt-2 text-[11px] text-[var(--color-sub)]">
                          まだ記録がありません。
                        </p>
                      ) : (
                        <>
                          <span className="mt-1.5 flex items-baseline gap-1">
                            <span className="text-xl font-bold tabular-nums">
                              {latest.weight.toFixed(1)}
                            </span>
                            <span className="text-[11px] text-[var(--color-sub)]">kg</span>
                          </span>
                          {goal === null ? (
                            isSelf ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setGoalOpen(true);
                                }}
                                className="mt-1 block text-[11px] text-[var(--color-accent)] underline"
                              >
                                目標を決める
                              </button>
                            ) : (
                              <span className="mt-1 block text-[11px] text-[var(--color-sub)]">
                                目標は未設定です。
                              </span>
                            )
                          ) : (
                            <>
                              <span
                                className="mt-0.5 block text-xs font-semibold tabular-nums"
                                style={{ color }}
                              >
                                {signed(-loss)} kg
                              </span>
                              <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-[var(--color-line)]">
                                <span
                                  className="block h-full rounded-full"
                                  style={{ width: `${String(ratio * 100)}%`, background: color }}
                                />
                              </span>
                              <span className="mt-1 block text-[10px] text-[var(--color-sub)]">
                                目標 {goal.goalWeight.toFixed(1)}kg まであと{" "}
                                {(rest ?? 0).toFixed(1)}kg
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 開始からの増減。体格の違う二人を同じ軸に重ねて「並走」を見せる */}
              {series.length > 0 && (
                <div className="rounded-xl border border-[var(--color-line)] bg-white p-3">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[11px] text-[var(--color-sub)]">開始からの増減</span>
                    <div className="flex gap-1">
                      {SPANS.map(({ days, label }) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => {
                            setSpan(days);
                          }}
                          aria-pressed={span === days}
                          className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${
                            span === days
                              ? "bg-[var(--color-sub)] text-[var(--color-bg)]"
                              : "border border-[var(--color-line)] text-[var(--color-sub)] hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-1.5">
                    <DietTrendChart dates={chartDates} series={series} logs={allLogs} />
                  </div>

                  <div className="flex flex-wrap gap-4 px-1 pt-0.5">
                    {series.map((item) => {
                      const latest = latestDietLogOf(allLogs, item.userId);
                      return (
                        <span
                          key={item.userId}
                          className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]"
                        >
                          <span
                            className="h-[3px] w-2.5 rounded-full"
                            style={{ background: item.color }}
                            aria-hidden
                          />
                          {item.name}
                          {latest !== null && (
                            <span className="tabular-nums" style={{ color: item.color }}>
                              {signed(latest.weight - item.baseline)}kg
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 摂取カロリー。増減グラフと日数の切り替え(span)を共用する */}
              {calorieSeries.length > 0 && (
                <div className="rounded-xl border border-[var(--color-line)] bg-white p-3">
                  <span className="block px-1 text-[11px] text-[var(--color-sub)]">
                    摂取カロリー
                  </span>

                  <div className="mt-1.5">
                    <DietCalorieChart dates={chartDates} series={calorieSeries} logs={allLogs} />
                  </div>

                  <div className="flex flex-wrap gap-4 px-1 pt-0.5">
                    {calorieSeries.map((item) => {
                      const latest = latestCalorieOf(item.userId);
                      return (
                        <span
                          key={item.userId}
                          className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]"
                        >
                          <span
                            className="h-[3px] w-2.5 rounded-full"
                            style={{ background: item.color }}
                            aria-hidden
                          />
                          {item.name}
                          {latest !== null && (
                            <span className="tabular-nums" style={{ color: item.color }}>
                              {latest}kcal
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* そろって記録した連続日数。片方だけでは伸びないので、相手に声をかける動機になる */}
              <div className="rounded-xl border border-[var(--color-line)] bg-white p-4">
                <span className="flex items-center gap-1.5 text-sm">
                  <Sparkles className="size-3.5 text-[var(--color-gold)]" aria-hidden />
                  ふたりそろって
                  <span className="text-base font-bold tabular-nums">{streak}</span>
                  日連続
                </span>

                <div className="mt-3 flex justify-between">
                  {dietDateRange(todayKey, 7).map((date) => (
                    <div key={date} className="flex flex-col items-center gap-1">
                      <div className="flex gap-0.5">
                        {[selfId, partnerId].map((userId, index) =>
                          userId === undefined ? (
                            // 相手が未参加の間も丸の数と幅を変えない(列がずれて見えるため)
                            <span key={`empty-${String(index)}`} className="size-2.5" />
                          ) : (
                            <span
                              key={userId}
                              className="size-2.5 rounded-full"
                              style={{
                                background: allLogs.some(
                                  (log) => log.authorId === userId && log.loggedOn === date,
                                )
                                  ? colorOf(userId)
                                  : "var(--color-line)",
                              }}
                              aria-label={`${dayLabelOf(date, todayKey, yesterdayKey)} ${nameOf(userId)}`}
                            />
                          ),
                        )}
                      </div>
                      <span className="text-[9.5px] text-[var(--color-sub)]">
                        {dayLabelOf(date, todayKey, yesterdayKey)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <span className="px-1 text-[11px] text-[var(--color-sub)]">きろく</span>

              <QueryState
                isPending={false}
                isError={false}
                errorMessage=""
                isEmpty={days.length === 0}
                emptyMessage="まだ記録がありません。今日の体重から始めてみましょう。"
                emptyClassName="py-10 text-center text-sm"
              />

              {days.map(([date, dayLogs]) => (
                <DietDayCard
                  key={date}
                  dayLabel={dayLabelOf(date, todayKey, yesterdayKey)}
                  entries={entriesOf(dayLogs)}
                  busyLogId={busyLogId}
                  onCheer={(log, kind) => void handleCheer(log, kind)}
                  onEdit={(log) => {
                    openLogForm(log.loggedOn, log);
                  }}
                  onDelete={(log) => void handleDeleteLog(log)}
                />
              ))}
            </>
          )}
        </div>

        {/* 一覧が伸びるほど下に沈む absolute ではなく、ビューポート基準の fixed で
            常にスクロール位置に関わらず親指の届く位置に留める(→ 共有リストと同じ形) */}
        <div className="pointer-events-none fixed inset-x-0 z-40 mx-auto flex max-w-[var(--content-w)] justify-end px-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] xl:hidden">
          <button
            type="button"
            onClick={() => {
              openLogForm(todayKey, selfToday);
            }}
            aria-label="記録する"
            className="pointer-events-auto flex size-[46px] items-center justify-center rounded-full bg-[var(--color-accent)] shadow-lg"
          >
            <Plus className="size-5 text-white" aria-hidden />
          </button>
        </div>
      </div>

      {logForm !== null && (
        <DietLogSheet
          editingLog={logForm.log}
          date={logForm.date}
          onDateChange={(date) => {
            openLogForm(
              date,
              allLogs.find((log) => log.authorId === selfId && log.loggedOn === date),
            );
          }}
          defaultWeight={defaultWeight}
          busy={saving}
          onSave={(input) => void handleSaveLog(input)}
          onClose={() => {
            setLogForm(null);
          }}
        />
      )}

      {goalOpen && (
        <DietGoalSheet
          selfGoal={selfGoal}
          partnerGoal={partnerGoal}
          partnerName={partnerId === undefined ? "相手" : nameOf(partnerId)}
          reward={reward.data ?? null}
          totalLoss={totalLoss}
          busy={saving}
          onSave={(goal, rewardInput) => void handleSaveGoal(goal, rewardInput)}
          onClose={() => {
            setGoalOpen(false);
          }}
        />
      )}
    </>
  );
}
